import React from 'react';
import { Box, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { AccountingRow, PaymentAccountingRow } from 'common/types';
import ContainerBox from 'components/ContainerBox';
import { useTranslation } from 'services/localization';
import {
	getCustomAccountingGroupsFromPaymentAccountingRows,
	getDatesFromPaymentAccountingRows,
	getItemNamesFromPaymentAccountingRows,
	getMethodsFromPaymentAccountingRows,
	getReceiptRows,
	getTaxesFromPaymentAccountingRows,
} from '../utils';
import { TableSection } from '../types';
import AccountingDataGrid from './AccountingDataGrid';

interface Props {
	rows: PaymentAccountingRow[];
	name: string;
	description?: string;
	startReceiptPrint: (receiptId: string) => void;
	tableSectionsToShow: TableSection[];
	getExportHeaders: (reportName: string) => string[];
	getExportFileName: (reportName: string) => string;
}

const AccountingTable = (props: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { rows, name, description, tableSectionsToShow } = props;
	const totalAmount = Math.round(rows.map((row) => row.totalAmount).reduce((a, b) => a + b, 0));
	const taxAmount = Math.round(rows.map((row) => row.taxAmount).reduce((a, b) => a + b, 0));
	const netAmount = totalAmount - taxAmount;

	const totalRow: AccountingRow = {
		id: `${name}_total`,
		units: rows.length,
		name: t('common:payment.total'),
		netAmount,
		taxAmount,
		taxPercent: null,
		totalAmount: totalAmount,
		paymentIds: [],
	};
	const taxRows = getTaxesFromPaymentAccountingRows(rows);
	const methodRows = getMethodsFromPaymentAccountingRows(rows, t);
	const customAccountingGroupRows = getCustomAccountingGroupsFromPaymentAccountingRows(rows);
	const rowsByDates = getDatesFromPaymentAccountingRows(rows);
	const itemNameRows = getItemNamesFromPaymentAccountingRows(rows);
	return (
		<ContainerBox padding="small">
			<Typography variant="h6" gutterBottom>
				{name}
			</Typography>
			<Typography variant="body2" gutterBottom color="textSecondary">
				{description}
			</Typography>
			<AccountingDataGrid
				rows={[totalRow]}
				exportHeaders={props.getExportHeaders(name)}
				exportFileName={props.getExportFileName(name)}
			/>
			<div className={classes.pageBreak} />
			{tableSectionsToShow.includes('date_breakdown') && !!rowsByDates.length && (
				<Box my={4}>
					<AccountingDataGrid
						rows={rowsByDates}
						tableName={t('reports.accounting.sections.date_breakdown', 'Date breakdown')}
						receiptRows={(tableRow) => rows.filter((row) => row.date === tableRow.name)}
						startReceiptPrint={props.startReceiptPrint}
						exportFileName={props.getExportFileName(`${name}_date_breakdown`)}
						exportHeaders={props.getExportHeaders(
							`${name} - ${t('reports.accounting.sections.date_breakdown', 'Date breakdown')}`,
						)}
					/>
				</Box>
			)}
			<div className={classes.pageBreak} />
			{tableSectionsToShow.includes('method_breakdown') && !!methodRows.length && (
				<Box my={4}>
					<AccountingDataGrid
						rows={methodRows}
						tableName={t(
							'reports.accounting.sections.method_breakdown',
							'Payment method breakdown',
						)}
						receiptRows={(tableRow) =>
							getReceiptRows(
								'method',
								tableRow.name,
								rows.filter((row) => tableRow.payment_ids.includes(row.paymentId)),
							)
						}
						startReceiptPrint={props.startReceiptPrint}
						exportFileName={props.getExportFileName(`${name}_method_breakdown`)}
						exportHeaders={props.getExportHeaders(
							`${name} - ${t(
								'reports.accounting.sections.method_breakdown',
								'Payment method breakdown',
							)}`,
						)}
					/>
				</Box>
			)}
			<div className={classes.pageBreak} />
			{tableSectionsToShow.includes('item_breakdown') && !!itemNameRows.length && (
				<Box my={4}>
					<AccountingDataGrid
						rows={itemNameRows}
						tableName={t('reports.accounting.sections.item_breakdown', 'Item breakdown')}
						receiptRows={(tableRow) =>
							getReceiptRows(
								'itemName',
								tableRow.name,
								rows.filter((row) => tableRow.payment_ids.includes(row.paymentId)),
							)
						}
						startReceiptPrint={props.startReceiptPrint}
						exportFileName={props.getExportFileName(`${name}_item_breakdown`)}
						exportHeaders={props.getExportHeaders(
							`${name} - ${t('reports.accounting.sections.item_breakdown', 'Item breakdown')}`,
						)}
					/>
				</Box>
			)}
			<div className={classes.pageBreak} />
			{tableSectionsToShow.includes('custom_group_breakdown') &&
				!!customAccountingGroupRows.length && (
					<Box my={4}>
						<AccountingDataGrid
							rows={customAccountingGroupRows}
							tableName={t(
								'reports.accounting.sections.custom_group_breakdown',
								'Custom group breakdown',
							)}
							exportFileName={props.getExportFileName(`${name}_group_breakdown`)}
							exportHeaders={props.getExportHeaders(
								`${name} - ${t(
									'reports.accounting.sections.custom_group_breakdown',
									'Custom group breakdown',
								)}`,
							)}
						/>
					</Box>
				)}
			<div className={classes.pageBreak} />
			{tableSectionsToShow.includes('tax_breakdown') && !!taxRows.length && (
				<Box my={4}>
					<AccountingDataGrid
						rows={taxRows}
						tableName={t('reports.accounting.sections.tax_breakdown', 'Tax breakdown')}
						exportFileName={props.getExportFileName(`${name}_tax_breakdown`)}
						exportHeaders={props.getExportHeaders(
							`${name} - ${t('reports.accounting.sections.tax_breakdown', 'Tax breakdown')}`,
						)}
					/>
				</Box>
			)}
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		table: {
			pageBreakInside: 'auto',
			margin: '16px 0 32px 0',
			tableLayout: 'fixed',
			minWidth: 640,
			'& tr': {
				pageBreakInside: 'avoid',
				pageBreakAfter: 'auto',
			},
			'& th, td': {
				borderBottom: `1px solid ${theme.palette.border.paper}`,
				minHeight: 42,
				padding: theme.spacing(1),
				pageBreakInside: 'avoid',
			},
			'& th:first-child': {
				width: '20%',
				paddingLeft: 0,
			},
			'& th:last-child': {
				paddingRight: 0,
				width: '10%',
				'@media print': {
					width: '0',
				},
			},
			'& td:first-child': {
				width: '20%',
				paddingLeft: 0,
			},
			'& td:last-child': {
				paddingRight: 0,
				width: '10%',
				'@media print': {
					width: '0',
				},
			},
		},
		innerTable: {
			margin: 0,
			'& th, td': {
				borderBottom: 'none',
				padding: theme.spacing(0.5, 1),
			},
		},
		dateTable: {
			'& td:first-child, th:first-child': {
				paddingLeft: 8,
			},
			'& td, th': {
				color: theme.palette.text.secondary,
			},
		},
		hideOnPrint: {
			'@media print': {
				display: 'none',
			},
		},
		pageBreak: {
			display: 'none',
			'@media print': {
				marginTop: '1rem',
				display: 'block',
				pageBreakBefore: 'auto',
			},
		},
	}),
);

export default AccountingTable;
